import { endpoints } from "@app/api/apis";
import { CreditApplicationSubType, CreditApplicationType } from "@app/configs/constants";
import handleApiErr from "@app/helper/handleApiErr";
import { BankAndMandateDetails, useBankAndMandate, useCreditLoanServicingApplication } from "@app/hooks/useBankAndMandate";
import { setApplicationId } from "@app/screens/FetchPortfolio/utils";
import Button from "@app/sharedComponents/Button/Button";
import { useAppSelector } from "@app/store/hooks";
import { RootState } from "@app/store/newstore";
import { updateUserDetailLinkedApp } from "@app/store/slices/authSlice";
import { showErrorSheet } from "@app/store/slices/errorSlice";
import { themeColorSet, ThemedView } from "@app/theme"
import { ThemedText } from "@app/theme/Components";
import axios from "axios";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { ActivityIndicator, Image, TouchableOpacity } from "react-native";
import LoanScreenShimmer from "@app/screens/LMSDasboard/components/ShimmerPlaceholder";
import StatusIcon from "../../../../../src/assets/svg/StatusIcon";
import { getNextRoute } from "@app/navigationApp/navigationUtil";
import MandateImage from "../../../../../src/assets/svg/MandateImage";
import CloseIcon from "@app/assets/svg/Close";
import { APPLICATION_STATUS, maskBankAccountNumber } from "@app/utils";
import HandsDown from "@app/assets/svg/HandDown";
import { CaretRight } from '@app/sharedComponents/Icons';

const ActivePill = () => {
    return (
        <ThemedView
            borderWidth={1}
            borderColor='border-success'
            backgroundColor='bg-fill-success'
            paddingTop='xs'
            paddingBottom='xs'
            paddingHorizontal='sm'
            borderRadius='xs'

            flexDirection="row"
            alignItems="center"
            justifyContent="center"
        >
            <ThemedText variant="b5s" color='text-success'>
                Active
            </ThemedText>
        </ThemedView>
    )
}

export const MandateNotificationPill = ({
    loading,
    updateMandate
}: {
    loading: number | undefined,
    updateMandate: ()=>void
}) => {
    return (
        <TouchableOpacity onPress={()=>!loading && updateMandate()}>
                <ThemedView 
                    paddingHorizontal='md' 
                    paddingVertical='md' 
                    flexDirection='row'
                    borderTopWidth={1} 
                    borderLeftWidth={1} 
                    borderRightWidth={1} 
                    borderColor='border-primary' 
                    borderTopLeftRadius='sm' 
                    borderTopRightRadius='sm'
                    alignItems='center'
                    style={{
                        backgroundColor: '#FFFFFF'
                    }}
                >
                    <HandsDown />
                    <ThemedView marginLeft='md'>
                        <ThemedText variant="b3s">
                            Your auto-debit is not active
                        </ThemedText>
                        <ThemedText variant="b4r" color='text-disabled'>
                            Setup mandate in less than 2 min
                        </ThemedText>
                    </ThemedView>
                    <ThemedView style={{flex: 1, justifyContent: 'flex-end', flexDirection: 'row'}}>
                        {
                            loading 
                            ? <ActivityIndicator />
                            : <CaretRight color={themeColorSet['bg-action-primary']}  size={24}/>
                        }
                    </ThemedView>
                    
                </ThemedView>
                <ThemedView borderBottomRightRadius='sm' borderBottomLeftRadius='sm' backgroundColor='bg-fill-warning' flexDirection='row' alignItems='center' padding='md' borderBottomWidth={1} borderLeftWidth={1} borderRightWidth={1} borderColor='border-primary'>
                    <ThemedView marginRight='md'> 
                        <StatusIcon size={16}/>
                    </ThemedView>
                    <ThemedText variant='b4s'>
                        Mandate ensures hassle-free EMI repayments
                    </ThemedText>
                </ThemedView>
        </TouchableOpacity>
    )
}

const RenderMandateActiveView = ({
    data,
    lenderId,
    updateBankMandate,
    loading
}: {
    data: BankAndMandateDetails | undefined
    lenderId: string,
    updateBankMandate: () => void
    loading: number | undefined
}) => {
    return (
        <>
            <ThemedView borderWidth={1} borderColor={'border-primary'} paddingHorizontal="md" paddingVertical="md" borderRadius="sm">
                <ThemedView paddingBottom='lg'>
                    <ThemedText variant='b2s' paddingBottom='xs'>
                        Primary bank account
                    </ThemedText>
                    <ThemedText variant='b4r' color='text-secondary'>
                        Default account for withdrawals & autopay
                    </ThemedText>
                </ThemedView>

                {/* Bank logo and icon */}
                <ThemedView flexDirection="row" alignItems="center" paddingBottom='md'>
                    <ThemedView paddingRight="sm">
                        <Image source={{ uri: `https://volt-images.s3.ap-south-1.amazonaws.com/bank-logos/pngs/${data?.bankAccountDetails.bankCode}.png` }} style={{ width: 40, height: 40 }} />
                    </ThemedView>
                    <ThemedView>
                        <ThemedText variant="b3s">
                            {data?.bankAccountDetails?.bankName}
                        </ThemedText>
                    </ThemedView>
                </ThemedView>

                <ThemedView flexDirection='row' justifyContent='space-between'>
                    <ThemedText variant='b4s'>
                        Account number
                    </ThemedText>
                    <ThemedText variant='b4r'>
                        {data?.bankAccountDetails?.accountNumber && maskBankAccountNumber(data?.bankAccountDetails?.accountNumber)}
                    </ThemedText>
                </ThemedView>
                <ThemedView style={{ borderWidth: 1, borderColor: `#F9F9FB` }} marginTop="sm" marginBottom='sm'></ThemedView>
                <ThemedView flexDirection='row' justifyContent='space-between'>
                    <ThemedText variant='b4s'>
                        IFSC Code
                    </ThemedText>
                    <ThemedText variant='b4r'>
                        {data?.bankAccountDetails?.ifscCode}
                    </ThemedText>
                </ThemedView>
                <ThemedView style={{ borderWidth: 1, borderColor: `#F9F9FB` }} marginTop="sm" marginBottom='sm'></ThemedView>
                <ThemedView paddingBottom="md">
                    <ThemedText variant='b4s' color='text-disabled'>
                        MANDATE DETAILS
                    </ThemedText>
                </ThemedView>
                <ThemedView flexDirection='row' justifyContent='space-between'>
                    <ThemedText variant='b4s'>
                        Mandate status
                    </ThemedText>
                    {
                        data?.creditMandateMapping?.isMandateSetup
                            ? <ActivePill />
                            : <></>
                    }
                </ThemedView>
                <ThemedView style={{ borderWidth: 1, borderColor: `#F9F9FB` }} marginTop="sm" marginBottom='sm'></ThemedView>
                <ThemedView flexDirection='row' justifyContent='space-between'>
                    <ThemedText variant='b4s'>
                        Mandate amount
                    </ThemedText>
                    <ThemedText variant='b4r'>
                        Monthly interest only
                    </ThemedText>
                </ThemedView>
                <ThemedView style={{ borderWidth: 1, borderColor: `#F9F9FB` }} marginTop="sm" marginBottom='sm'></ThemedView>
                <ThemedView flexDirection='row' justifyContent='space-between'>
                    <ThemedText variant='b4s'>
                        Auto debit date
                    </ThemedText>
                    <ThemedText variant='b4r'>
                        {lenderId === 'Tata' ? `3rd of every month` : `7th of every month`}
                    </ThemedText>
                </ThemedView>
                <ThemedView style={{ borderWidth: 1, borderColor: `#F9F9FB` }} marginTop="sm" marginBottom='sm'></ThemedView>
                <ThemedView flexDirection='row' justifyContent='space-between' paddingBottom="md">
                    <ThemedText variant='b4s'>
                        Mandate type
                    </ThemedText>
                    <ThemedText variant='b4r'>
                        E-NACH
                    </ThemedText>
                </ThemedView>
                {
                    data?.isMandateUpdateAllowed &&
                    <Button
                        eventName={'MANDATE_REGISTRATION_BTN_CLICKED'}
                        label={'Update bank mandate'}
                        type={'OUTLINED'}
                        handlePress={updateBankMandate}
                        buttonLoader={loading === 3}
                    />
                }
            </ThemedView>
        </>
    )
}

const RenderNoBankAddedView = ({
    updateBankMandate,
    loading
}: {
    updateBankMandate: () => void
    loading: number | undefined
}) => {
    return (
        <>
            <ThemedView borderWidth={1} borderColor={'border-primary'} paddingHorizontal="md" paddingVertical="md" borderRadius="sm">
                <ThemedView paddingBottom='lg'>
                    <ThemedText variant='b2s' paddingBottom='xs'>
                        Primary bank account
                    </ThemedText>
                    <ThemedText variant='b4r' color='text-secondary'>
                        Default account for withdrawals & autopay
                    </ThemedText>
                </ThemedView>

                {/* Bank logo and icon */}
                <ThemedView flexDirection="row" padding="md" backgroundColor='bg-fill-warning' borderRadius='xs' marginBottom="md">
                    <ThemedView marginRight="sm">
                        <StatusIcon size={16} />
                    </ThemedView>
                    <ThemedView >
                        <ThemedText variant="b4r">
                            Add your bank account to receive withdrawals and repay EMIs easily
                        </ThemedText>
                    </ThemedView>
                </ThemedView>
                {
                    <Button
                        eventName={'MANDATE_REGISTRATION_BTN_CLICKED'}
                        label={'Add account'}
                        type={'SOLID'}
                        handlePress={updateBankMandate}
                        buttonLoader={loading === 1}
                    />
                }
            </ThemedView>
        </>
    )
}

const RenderMandateNotRegisteredView = ({
    data,
    lenderId,
    updateBankMandate,
    loading
}: {
    data: BankAndMandateDetails | undefined
    lenderId: string,
    updateBankMandate: () => void
    loading: number | undefined
}) => {
    return (
        <>
            <ThemedView borderWidth={1} borderColor={'border-primary'} paddingHorizontal="md" paddingVertical="md" borderRadius="sm">
                <ThemedView paddingBottom='lg'>
                    <ThemedText variant='b2s' paddingBottom='xs'>
                        Primary bank account
                    </ThemedText>
                    <ThemedText variant='b4r' color='text-secondary'>
                        Default account for withdrawals & autopay
                    </ThemedText>
                </ThemedView>

                {/* Bank logo and icon */}
                <ThemedView flexDirection="row" alignItems="center" paddingBottom='md'>
                    <ThemedView paddingRight="sm">
                        <Image source={{ uri: `https://volt-images.s3.ap-south-1.amazonaws.com/bank-logos/pngs/${data?.bankAccountDetails.bankCode}.png` }} style={{ width: 40, height: 40 }} />
                    </ThemedView>
                    <ThemedView>
                        <ThemedText variant="b3s">
                            {data?.bankAccountDetails?.bankName}
                        </ThemedText>
                    </ThemedView>
                </ThemedView>

                <ThemedView flexDirection='row' justifyContent='space-between'>
                    <ThemedText variant='b4s'>
                        Account number
                    </ThemedText>
                    <ThemedText variant='b4r'>
                        {data?.bankAccountDetails?.accountNumber && maskBankAccountNumber(data?.bankAccountDetails?.accountNumber)}
                    </ThemedText>
                </ThemedView>
                <ThemedView style={{ borderWidth: 1, borderColor: `#F9F9FB` }} marginTop="sm" marginBottom='sm'></ThemedView>
                <ThemedView flexDirection='row' justifyContent='space-between'>
                    <ThemedText variant='b4s'>
                        IFSC Code
                    </ThemedText>
                    <ThemedText variant='b4r'>
                        {data?.bankAccountDetails?.ifscCode}
                    </ThemedText>
                </ThemedView>
                <ThemedView style={{ borderWidth: 1, borderColor: `#F9F9FB` }} marginTop="sm" marginBottom='sm'></ThemedView>
                <ThemedView paddingBottom="md">
                    <ThemedText variant='b4s' color='text-disabled'>
                        MANDATE DETAILS
                    </ThemedText>
                </ThemedView>
                <ThemedView flexDirection="row" padding="md" backgroundColor='bg-fill-warning' borderRadius='xs' marginBottom="md">
                    <ThemedView marginRight="sm">
                        <StatusIcon size={16} />
                    </ThemedView>
                    <ThemedView style={{flex: 1}}>
                        <ThemedText variant="b4r">
                            <ThemedText variant="b4b" marginRight='xs'>
                                Mandate{' '}
                            </ThemedText>
                            <ThemedText variant="b4r">
                                ensures your EMIs are always paid on time, helping you avoid penalties & improve credit score.
                            </ThemedText>
                        </ThemedText>
                    </ThemedView>
                </ThemedView>
                {
                    data?.isMandateUpdateAllowed &&
                    <Button
                        eventName={'MANDATE_REGISTRATION_BTN_CLICKED'}
                        label={'Continue setting up'}
                        type={'SOLID'}
                        handlePress={updateBankMandate}
                        buttonLoader={loading === 2}
                    />
                }
            </ThemedView>
        </>
    )
}

const RenderInProgressCard = ({
    data,
    updateBankMandate,
    loading,
    show
}: {
    data: BankAndMandateDetails | undefined,
    updateBankMandate: () => void;
    loading: number | undefined,
    show: boolean
}) => {
    const [_show, setShow] = useState(show);
    return _show && (
        <ThemedView flexDirection="column" width={'100%'} paddingHorizontal='lg' paddingBottom='lg' borderWidth={1} borderColor='border-primary' backgroundColor='bg-fill-emphasis-primary-1' borderRadius='sm' marginBottom='md'>
            <ThemedView flexDirection='row-reverse' position='relative' height={20} top={10} left={5}>
                <ThemedView>
                    <TouchableOpacity onPress={()=>setShow(!_show)}>
                        <CloseIcon />
                    </TouchableOpacity>
                </ThemedView>
            </ThemedView>
            <ThemedView flexDirection="row" width={'100%'} justifyContent='space-between' marginBottom='md'>
                <ThemedView maxWidth={221}>
                    <ThemedText variant="b3s">
                        Almost there!
                    </ThemedText>
                    <ThemedView paddingBottom='xs'></ThemedView>
                    <ThemedText variant="b4r">
                        You’r new bank account is verified. Set up auto-pay & you’re all set.
                    </ThemedText>
                </ThemedView>
                <ThemedView>
                    <MandateImage />
                </ThemedView>
            </ThemedView>
            {
                data?.isMandateUpdateAllowed &&
                <Button
                    eventName={'MANDATE_REGISTRATION_BTN_CLICKED'}
                    label={'Continue'}
                    type={'SOLID'}
                    handlePress={updateBankMandate}
                    buttonLoader={loading === 4}
                />
            }
        </ThemedView>
    )
}

const BankAndMandate = ({ navigation, route, isNotification = false }: { navigation: any, route: any, isNotification: boolean }) => {
    const [loading, setLoading] = useState<undefined | number>(undefined);
    const { data, isLoading } = useBankAndMandate();
    const { AllLoanServicingApplications } = useCreditLoanServicingApplication();
    const dispatch = useDispatch();
    const userDetails = useAppSelector((state: RootState) => state.auth.userDetails);

    const updateBankMandate = (eventOrder: number) => {
        setLoading(eventOrder);
        axios.post(
            `${endpoints.creditLoanServicingApplication}${userDetails?.linkedCredits?.[0]?.creditId}`,
            {
                creditApplicationType: CreditApplicationType.CREDIT_LOAN_SERVICING_APPLICATION_AGAINST_SECURITIES,
                creditApplicationSubType: CreditApplicationSubType.MANDATE_UPDATE
            }
        ).then(async (res: any) => {
            dispatch(updateUserDetailLinkedApp(res?.data));
            await setApplicationId(res?.data?.applicationId);
            const nextStepId = getNextRoute(
                res?.data?.currentStepId,
                res?.data?.applicationState,
                res?.data?.stepStatusMap
            )
            navigation.navigate(nextStepId);
        }).catch((err: any) => {
            if (handleApiErr(err)) {
                dispatch(showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: err?.data?.message,
                    closeOnBackdropPress: true,
                    errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                    errorHeading: err?.data?.header,
                    showErrorHeading: true,
                    btnName: 'Ok, got it!',
                }))
            }
        }).finally(() => {
            setLoading(undefined);
        })
    }

    if(isNotification) {
        return <MandateNotificationPill 
                loading={loading}
                updateMandate={()=>updateBankMandate(5)}
            />
    }

    const getScreen = () => {
        if (data?.bankAccountDetails === null) {
            return <RenderNoBankAddedView
                loading={loading}
                updateBankMandate={()=>updateBankMandate(1)}
            />
        } else if (
            data?.creditMandateMapping === null ||
            data?.creditMandateMapping?.isMandateSetup === null ||
            data?.creditMandateMapping?.isMandateSetup === false
        ) {
            return <RenderMandateNotRegisteredView
                data={data}
                loading={loading}
                lenderId={userDetails?.linkedCredits?.[0]?.lendingPartnerId}
                updateBankMandate={()=>updateBankMandate(2)}
            />
        } else {
            return <RenderMandateActiveView
                data={data}
                loading={loading}
                lenderId={userDetails?.linkedCredits?.[0]?.lendingPartnerId}
                updateBankMandate={()=>updateBankMandate(3)}
            />
        }
    };

    const showShouldInProgressMandate = AllLoanServicingApplications && AllLoanServicingApplications?.length > 0 && AllLoanServicingApplications.some((application: any)=>(
        application?.applicationState === APPLICATION_STATUS.IN_PROGRESS &&
        application?.applicationType === CreditApplicationType.CREDIT_LOAN_SERVICING_APPLICATION_AGAINST_SECURITIES &&
        application?.creditApplicationSubType === CreditApplicationSubType.MANDATE_UPDATE &&
        application?.stepStatusMap?.BANK_ACCOUNT_VERIFICATION === 'COMPLETED'
    ));

    return isLoading ? (
        <>
            <LoanScreenShimmer forBankMandate={true} />
        </>
    ) : (
        <>
            <ThemedText variant="b4s" color="text-secondary" my="lg">BANK DETAILS</ThemedText>
            <RenderInProgressCard
                data={data}
                loading={loading}
                updateBankMandate={()=>updateBankMandate(4)}
                show={showShouldInProgressMandate}
            />
            {getScreen()}
        </>
    )
}

export default BankAndMandate;